const root = document.documentElement;

const HERO_SECTION_MATCH =
  '.hero, .hero-panels, .video-hero, [class*="hero-panels"], [class*="video-hero"], .main-collection--banner';

function firstSectionHasHero() {
  const first = document.querySelector('#MainContent > .shopify-section:first-child');
  if (!first) return false;
  return Boolean(first.querySelector(HERO_SECTION_MATCH) || first.matches('.shopify-section:has(.hero, .hero-panels, .video-hero, .main-collection--banner)'));
}

class HeaderComponent extends HTMLElement {
  connectedCallback() {
    if (this._ready) {
      this.reconnect();
      return;
    }
    this._ready = true;
    this.section = this.closest('.header-section') || this.parentElement;
    this.sticky = this.dataset.sticky || 'none';
    this.transparentMode = this.dataset.transparentMode || 'off';
    this.transparent = this.hasAttribute('data-transparent');
    if (this.classList.contains('header--transparent-detect')) {
      this.transparent = firstSectionHasHero();
      this.classList.toggle('header--transparent', this.transparent);
      this.toggleAttribute('data-transparent', this.transparent);
      this.section?.classList.toggle('header-section--transparent', this.transparent);
    }
    this.drawer = this.querySelector('[data-drawer]');
    this.opener = this.querySelector('[data-drawer-open]');
    this.lastY = window.scrollY;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.observeHeight();
    this.initDrawer();
    this.initAccordions();
    this.initHoverDropdowns();
    this.initCart();
    this.onSearchToggle = () => this.update();
    this.onCartToggle = () => this.update();
    document.addEventListener('search:opened', this.onSearchToggle);
    document.addEventListener('search:closed', this.onSearchToggle);
    document.addEventListener('cart:open', this.onCartToggle);
    document.addEventListener('cart:close', this.onCartToggle);

    if (this.transparent) {
      const solid = () => this.classList.add('is-hovered');
      const clear = () => {
        if (!this.contains(document.activeElement) && !this.matches(':hover')) this.classList.remove('is-hovered');
        this.update();
      };
      this.addEventListener('pointerenter', () => {
        solid();
        this.update();
      });
      this.addEventListener('pointerleave', clear);
      this.addEventListener('focusin', () => {
        solid();
        this.update();
      });
      this.addEventListener('focusout', () => requestAnimationFrame(clear));
    }

    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('cart:updated', this.onCartUpdated);
    document.removeEventListener('search:opened', this.onSearchToggle);
    document.removeEventListener('search:closed', this.onSearchToggle);
    document.removeEventListener('cart:open', this.onCartToggle);
    document.removeEventListener('cart:close', this.onCartToggle);
    this.resizeObserver?.disconnect();
    root.classList.remove('header-is-hidden');
  }

  reconnect() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.onCartUpdated) document.addEventListener('cart:updated', this.onCartUpdated);
    document.addEventListener('search:opened', this.onSearchToggle);
    document.addEventListener('search:closed', this.onSearchToggle);
    document.addEventListener('cart:open', this.onCartToggle);
    document.addEventListener('cart:close', this.onCartToggle);
    this.resizeObserver?.observe(this);
    this.update();
  }

  observeHeight() {
    const set = () => root.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
    set();
    if (!('ResizeObserver' in window)) return;
    this.resizeObserver = new ResizeObserver(set);
    this.resizeObserver.observe(this);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  hasOpenLayer() {
    const searchOpen = document.querySelector('[data-search-overlay][open]');
    const cartOpen = document.querySelector('cart-drawer dialog[open]');
    return Boolean(this.drawer?.open || this.querySelector('details[data-dropdown][open]') || searchOpen || cartOpen);
  }

  update() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this.lastY;
    const height = this.offsetHeight;
    const top = this.section.getBoundingClientRect().top;
    const stuck = this.sticky !== 'none' && y > 0 && top <= 0.5;

    this.classList.toggle('is-stuck', stuck);

    if (this.transparent) {
      const solid = y > 8 || this.classList.contains('is-hovered') || this.hasOpenLayer();
      this.classList.toggle('is-solid', solid);
    }

    if (this.sticky === 'scroll_up') {
      const offset = this.naturalTop() + height;
      if (y <= offset || this.hasOpenLayer()) {
        this.setHidden(false);
      } else if (delta > 4) {
        this.setHidden(true);
      } else if (delta < -4) {
        this.setHidden(false);
      }
    }

    this.lastY = y;
  }

  naturalTop() {
    let total = 0;
    let element = this.section.previousElementSibling;
    while (element) {
      if (element.classList.contains('shopify-section')) total += element.offsetHeight;
      element = element.previousElementSibling;
    }
    return total;
  }

  setHidden(hidden) {
    if (this.section.classList.contains('is-hidden') === hidden) return;
    this.section.classList.toggle('is-hidden', hidden);
    root.classList.toggle('header-is-hidden', hidden);
    if (hidden) this.querySelectorAll('details[data-dropdown][open]').forEach((details) => (details.open = false));
  }

  initDrawer() {
    if (!this.drawer || !this.opener) return;
    this.opener.addEventListener('click', () => this.openDrawer());
    this.drawer.querySelectorAll('[data-drawer-close]').forEach((button) =>
      button.addEventListener('click', () => this.closeDrawer())
    );
    this.drawer.addEventListener('click', (event) => {
      if (event.target === this.drawer) this.closeDrawer();
    });
    this.drawer.addEventListener('close', () => {
      window.Theme?.lockScroll?.(false);
      this.opener.setAttribute('aria-expanded', 'false');
      this.update();
      this.opener.focus({ preventScroll: true });
    });
    this.drawer.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDrawer();
    });
  }

  openDrawer() {
    if (this.drawer.open) return;
    this.focusReturn = document.activeElement;
    this.drawer.showModal();
    window.Theme?.lockScroll?.(true);
    this.opener.setAttribute('aria-expanded', 'true');
    this.update();
    this.drawer.querySelector('[data-drawer-close]')?.focus({ preventScroll: true });
  }

  closeDrawer() {
    if (this.drawer.open) this.drawer.close();
  }

  initAccordions() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.querySelectorAll('details[data-accordion]').forEach((details) => {
      const summary = details.querySelector('summary');
      const panel = details.querySelector('[data-accordion-panel]');
      if (!summary || !panel) return;
      details.classList.toggle('is-open', details.open);

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (details.dataset.animating) return;
        const opening = !details.classList.contains('is-open');

        if (reduceMotion.matches || !panel.animate) {
          details.open = opening;
          details.classList.toggle('is-open', opening);
          return;
        }

        details.dataset.animating = 'true';
        if (opening) details.open = true;
        details.classList.toggle('is-open', opening);
        const height = panel.scrollHeight;
        const animation = panel.animate(
          { height: opening ? ['0px', `${height}px`] : [`${height}px`, '0px'], opacity: opening ? [0, 1] : [1, 0] },
          { duration: Math.min(480, 240 + height * 0.4), easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        );
        animation.onfinish = animation.oncancel = () => {
          if (!opening) details.open = false;
          delete details.dataset.animating;
        };
      });

      details.addEventListener('toggle', () => {
        if (!details.dataset.animating) details.classList.toggle('is-open', details.open);
      });
    });
  }

  initHoverDropdowns() {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
    this.querySelectorAll('.header__nav details[data-dropdown]').forEach((details) => {
      let timer;
      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !canHover.matches) return;
        clearTimeout(timer);
        details.open = true;
      });
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !canHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (!details.matches(':has(:focus-visible)')) details.open = false;
          this.update();
        }, 120);
      });
      details.querySelector('summary')?.addEventListener('click', (event) => {
        if (canHover.matches && details.open && details.matches(':hover')) event.preventDefault();
      });
    });
  }

  initCart() {
    this.cartLink = this.querySelector('[data-header-cart]');
    if (!this.cartLink) return;
    this.cartBadge = this.cartLink.querySelector('[data-cart-count]');
    this.cartLive = this.querySelector('[data-cart-live]');
    this.cartCount = Number(this.cartBadge?.textContent.replace(/\D/g, '') || 0);
    this.onCartUpdated = this.onCartUpdated.bind(this);
    document.addEventListener('cart:updated', this.onCartUpdated);
  }

  onCartUpdated(event) {
    const count = Number(event.detail?.cart?.item_count);
    if (!Number.isFinite(count)) return;
    const previous = this.cartCount;
    this.cartCount = count;

    requestAnimationFrame(() => {
      if (this.cartBadge) {
        this.cartBadge.textContent = count > 99 ? '99+' : String(count);
        this.cartBadge.hidden = count === 0;
      }

      const template = count === 1 ? this.cartLink.dataset.labelOne : this.cartLink.dataset.labelOther;
      if (template) {
        const label = template.replace(/\d+/, String(count));
        this.cartLink.setAttribute('aria-label', label);
        if (this.cartLive && count !== previous) this.cartLive.textContent = label;
      }

      if (count > previous) {
        this.cartLink.classList.remove('is-bumping');
        void this.cartLink.offsetWidth;
        this.cartLink.classList.add('is-bumping');
        clearTimeout(this.bumpTimer);
        this.bumpTimer = setTimeout(() => this.cartLink.classList.remove('is-bumping'), 650);
      }
    });
  }
}

if (!customElements.get('header-component')) customElements.define('header-component', HeaderComponent);

if (!window.__themeDropdowns) {
  window.__themeDropdowns = true;

  const closeAll = (except) => {
    document.querySelectorAll('details[data-dropdown][open]').forEach((details) => {
      if (details !== except && !details.contains(except)) details.open = false;
    });
  };

  document.addEventListener('click', (event) => {
    const current = event.target.closest('details[data-dropdown]');
    closeAll(current);
  });

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-dropdown]') || !details.open) return;
      closeAll(details);
      details.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const details = event.target.closest?.('details[data-dropdown][open]');
    if (!details) return;
    event.preventDefault();
    event.stopPropagation();
    details.open = false;
    details.querySelector('summary')?.focus();
  });

  document.addEventListener('focusout', (event) => {
    const details = event.target.closest?.('details[data-dropdown][open]');
    if (details && event.relatedTarget && !details.contains(event.relatedTarget)) details.open = false;
  });
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:unload', () => root.classList.remove('header-is-hidden'));
}
